/* eslint-disable prettier/prettier */
import React, { useEffect, useState, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { JSX } from 'react';
import { renderAsync } from 'docx-preview';
import {
    ArrowLeft,
    Download,
    Lock,
    AlertCircle,
    CheckCircle,
    ChevronDown,
    Linkedin,
    Mail,
    Pencil,
} from 'lucide-react';
import { useCourseDetails } from '../../hooks/useCourse';
import { useFAQArticles, useFAQActions } from '../../hooks/useFaq';
import { useUserProfile } from '../../hooks/useUserProfile';
import styles from '../../styles/pages/admin/AdminEditProgramPage.module.css';
import ProgramPlaceholderImage from '../../assets/images/ProgramMainImg.jpg';
import { useHeader } from '../../context/HeaderContext';
import { ReactComponent as ProgramOwner } from '../../assets/icons/ProgramOwner.svg';
import { ReactComponent as ProgramFormat } from '../../assets/icons/ProgramFormat.svg';
import { ReactComponent as ProgramCertification } from '../../assets/icons/ProgramCertification.svg';
import { useCoachesList } from '../../hooks/useCoachesList';
import {
    useResourceActions,
    useResources,
    useUploadResource,
} from '../../hooks/useResource';
import { formatFileSize } from '../../utils/resourceFileSize';
import ConfirmDownloadModal from '../../components/controlVariations/ConfirmDownloadModal';
import { useCourseAssessments } from '../../hooks/useAssessment';
import { useSubmitAssessment } from '../../hooks/useSubmitAssessment';
import AssessmentStatusCell from '../../components/assessment/AssessmentStatusCell';
import { useRef } from 'react';
import { useAssessmentStatus } from '../../hooks/useAssessmentStatus';
import { AssessmentRow } from '../../components/assessment/AssessmentRow';
import { useDeleteAssessment } from '../../hooks/useDeleteAssessment';
import { useCourseAssessmentStatuses } from '../../hooks/useCourseAssessmentStatuses';
import { useDeleteAssessmentSubmission } from '../../hooks/useDeleteAssessmentSubmission';
import DeleteConfirmModal from '../../components/controlVariations/ConfirmDelete';
import SuccessModal from '../../components/controlVariations/UploadSuccess';
import PopupMobileModal from '../../components/common/PopupMobileModal';
import ResponsiveModal from '../../components/controlVariations/ResponsiveModal';
import { AssessmentStatus } from '../../types/assessment';
import ErrorPopupModal from '../../components/controlVariations/ErrorPopupMoadal';
import { getApiErrorMessage } from '../../utils/getApiErrorMessage';
import ResourceManager, {
    ResourceItem,
} from '../../components/pageComponents/dashboard/coach/ResourceManager';
import AssignmentForm from '../../components/pageComponents/dashboard/coach/AssignmentForm';
import { useCreateAssessment } from '../../hooks/useCreateAssessment';
import ResourceModal, {
    ResourceFile,
} from '../../components/pageComponents/dashboard/coach/ResourceModal';
import SuccessContent from '../../components/controlVariations/ModularSuccessModal';
import ConfirmContent from '../../components/controlVariations/ModularConfirmDelete';
import { useCourseModules } from '../../hooks/useCourseModules';
import { useResource } from '../../hooks/useResource';
import Tooltip from '../../components/common/ToolTip';
import ProgramContent from '../../components/pageComponents/program/ProgramContent';
import EditProgramInfo from '../../components/pageComponents/program/EditProgramInfo';
import FaqModal from '../../components/pageComponents/program/EditProgramFaq';
import ContentModal from '../../components/pageComponents/program/EditProgramContent';
import LearningsModal from '../../components/pageComponents/program/EditProgramLearnings';
import OwnerModal from '../../components/pageComponents/program/EditProgramOwner';
import { ProgramContentBuilder } from '../../components/pageComponents/program/ProgramContentBuilder';
import { useUpdateCourse } from '../../hooks/useUpdateCourse';
import UserPlaceholder from '../../assets/images/userPlaceHolder.png';
import { createPortal } from 'react-dom';
import { apiService } from '../../services/api.service';
import ProgramGoals from '../../components/pageComponents/program/content/ProgramGoals';
import goalStyles from '../../styles/components/pageComponents/program/ProgramGoals.module.css';
import { ProgramGoalsModal } from '../../components/pageComponents/program/ProgramGoalsModal';
import { EditProgramGoals } from '../../components/pageComponents/program/content/EditProgramGoals';
import { useGoal } from '../../hooks/useGoal';
import { useGoalType } from '../../hooks/useGoalType';
import { formatActivityDateTime, formatModuleDate } from '../../components/pageComponents/program/ProgramContent';
import { FILE_TYPE_MAP, getFileTypeLabel } from '../../utils/fileTypeMap';
import ViewLearnerUploads from '../../components/pageComponents/ViewLearnerUploads';
import { useCourseSubmissions } from '../../hooks/useCourseSubmissions';
import { useAzureFileDownload } from '../../hooks/useAzureFileDownload';
import type { LearnerUpload } from '../../components/pageComponents/ViewLearnerUploads';
import { mapSubmissionsToLearnerUploads } from '../../utils/learnerUploads';
import { useReviewAssessmentSubmission } from '../../hooks/useReviewAssessmentSubmission';
// Inside your component function:

const AdminEditProgramPage: React.FC = () => {

    const triggerRef = useRef<HTMLDivElement | null>(null);

    const EditableSection: React.FC<{
        title?: string;
        children: React.ReactNode;
        onEdit?: () => void;
    }> = ({ title, children, onEdit }) => {
        return (
            <div className={styles.editableSection}>
                {title && <h3 className={styles.sectionTitle}>{title}</h3>}

                <div className={styles.sectionContent}>
                    {children}

                    {onEdit && (
                        <button
                            className={styles.editBtn}
                            onClick={onEdit}
                        >
                            Edit
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="22"
                                height="22"
                                viewBox="0 0 22 22"
                                fill="none"
                            >
                                {<Pencil size={22} />}
                            </svg>
                        </button>
                    )}
                </div>
            </div>
        );
    };
    const { setHeaderContent } = useHeader();
    // Set header content on mount
    useEffect(() => {
        console.log('🔥 setting header');
        setHeaderContent(
            <div
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    width: '100%',
                }}
            >
                <h2 style={{ fontSize: '20px', fontWeight: '600', margin: 0 }}>
                    Programs Details
                </h2>
            </div>
        );
        return () => {
            setHeaderContent(null); // cleanup on unmount
        };
    }, []);

    const user = JSON.parse(
        localStorage.getItem('user') || '{}'
    );

    const { id } = useParams<{ id: string }>();


    const {
        submissions,
        loading: submissionsLoading,
        error: submissionsError,
        fetchSubmissions,
    } = useCourseSubmissions(id);

    const {
        reviewSubmission,
        loading: reviewingSubmission,
    } = useReviewAssessmentSubmission();


    const learnerUploads: LearnerUpload[] = useMemo(
        () => mapSubmissionsToLearnerUploads(submissions),
        [submissions]
    );

    const {
        downloadFile: downloadLearnerFile,
        downloading: downloadingLearnerFile,
    } = useAzureFileDownload();

    const handleViewLearnerUploads = async () => {
        if (!id) {
            console.error(
                '❌ Cannot fetch submissions: course ID is missing'
            );

            showError(
                'Course ID is missing. Cannot load learner uploads.'
            );

            return;
        }

        try {
            setShowLearnerUploads(true);

            await fetchSubmissions({
                page: 1,
                limit: 100,
            });
        } catch (err) {
            console.error(
                '❌ Learner submissions fetch failed:',
                err
            );

            showError(
                getApiErrorMessage(err)
            );
        }
    };

      const handleViewLearnerUpload = async (
        upload: LearnerUpload
      ): Promise<void> => {
        const fileType = upload.fileType
          ?.toLowerCase()
          .trim()
          .replace('.', '');
    
        // Safety check
        if (!['pdf', 'doc', 'docx'].includes(fileType)) {
          return;
        }
    
        if (!upload.fileId) {
          showError('File ID is missing. Unable to open this file.');
          return;
        }
    
        // Open tab immediately so browser doesn't block it
        const newTab = window.open('', '_blank');
    
        if (!newTab) {
          showError('Please allow pop-ups in your browser to view the file.');
          return;
        }
    
        try {
          console.log('👁️ Viewing learner upload:', {
            fileId: upload.fileId,
            fileType: upload.fileType,
            uploadedBy: upload.uploadedBy,
          });
    
          const result = await downloadLearnerFile(upload.fileId);
    
          if (!result?.file) {
            newTab.close();
            showError('Unable to retrieve the file for viewing.');
            return;
          }
    
          /*
           * result.file format:
           * data:application/pdf;base64,....
           *
           * Convert base64/data URL -> Blob
           */
          const [header, base64Data] = result.file.split(',');
    
          if (!header || !base64Data) {
            newTab.close();
            showError('Invalid file data received from server.');
            return;
          }
    
          const mimeType =
            header.match(/data:(.*?);base64/)?.[1] ||
            result.mimetype ||
            'application/octet-stream';
    
          const byteCharacters = atob(base64Data);
    
          const byteNumbers = new Array(byteCharacters.length);
    
          for (let i = 0; i < byteCharacters.length; i++) {
            byteNumbers[i] = byteCharacters.charCodeAt(i);
          }
    
          const byteArray = new Uint8Array(byteNumbers);
    
          const blob = new Blob([byteArray], {
            type: mimeType,
          });
    
          const blobUrl = URL.createObjectURL(blob);
    
          /*
           * PDF
           * ----
           * Browser can render PDF blobs natively.
           */
          if (fileType === 'pdf') {
            newTab.location.href = blobUrl;
    
            /*
             * Keep the blob alive for the newly opened tab.
             * Do not revoke immediately.
             */
            setTimeout(() => {
              URL.revokeObjectURL(blobUrl);
            }, 60 * 1000);
    
            return;
          }
    
          /*
           * DOC / DOCX
           * ----------
           * Browser cannot natively render Word documents.
           * We will render these in the newly opened tab.
           */
          if (fileType === 'docx' || fileType === 'doc') {
            newTab.document.open();
    
            newTab.document.write(`
        <!DOCTYPE html>
        <html>
          <head>
            <title>${result.filename || upload.assignmentName}</title>
    
            <meta charset="UTF-8" />
    
            <meta
              name="viewport"
              content="width=device-width, initial-scale=1.0"
            />
    
            <style>
              body {
                margin: 0;
                padding: 24px;
                
                font-family: Arial, sans-serif;
              }
    
              #document-container {
                width: 100%;
                max-width: 1000px;
                margin: 0 auto;
              }
            </style>
          </head>
    
          <body>
            <div id="document-container">
              <p>Loading document...</p>
            </div>
          </body>
        </html>
      `);
    
            newTab.document.close();
    
            const container =
              newTab.document.getElementById('document-container');
    
            if (!container) {
              newTab.close();
              showError('Unable to create document preview.');
              return;
            }
    
            container.innerHTML = '';
    
            const arrayBuffer = await blob.arrayBuffer();
    
            await renderAsync(
              arrayBuffer,
              container,
              undefined,
              {
                className: 'docx-preview',
                inWrapper: true,
                breakPages: true,
                ignoreWidth: false,
                ignoreHeight: false,
                ignoreFonts: false,
              }
            );
    
            URL.revokeObjectURL(blobUrl);
    
            return;
          }
    
          URL.revokeObjectURL(blobUrl);
          newTab.close();
    
          showError('This file type cannot be previewed.');
        } catch (err: any) {
          console.error('❌ Learner file view failed:', err);
    
          newTab.close();
    
          showError(getApiErrorMessage(err));
        }
      };

    const handleSaveLearnerApproval = async (
        upload: LearnerUpload,
        isApproved: boolean
    ): Promise<void> => {
        try {
            if (!upload.submissionId) {
                throw new Error(
                    'Submission ID is missing. Unable to review submission.'
                );
            }

            console.log('📝 Reviewing learner submission:', {
                submissionId: upload.submissionId,
                passed: isApproved,
                score: 100,
                maxscore: 100,
            });

            const response = await reviewSubmission(
                upload.submissionId,
                {
                    score: 100,
                    maxscore: 100,
                    passed: isApproved,
                }
            );

            if (!response) {
                throw new Error(
                    'Failed to review learner submission.'
                );
            }

            // Refresh data while keeping View Learner Uploads open
            await fetchSubmissions({
                page: 1,
                limit: 100,
            });

            // Show dedicated review success popup
            setSubmissionReviewSuccess({
                open: true,
                title: isApproved
                    ? 'Submission Passed Successfully'
                    : 'Submission Failed Successfully',
                subtitle: isApproved
                    ? 'The learner submission has been marked as passed.'
                    : 'The learner submission has been marked as failed.',
            });

            setTimeout(() => {
                setSubmissionReviewSuccess(prev => ({
                    ...prev,
                    open: false,
                }));
            }, 2000);
        } catch (err: any) {
            console.error(
                '❌ Learner submission review failed:',
                err
            );

            showError(err);

            // Important:
            // ViewLearnerUploads stays in edit mode on failure.
            throw err;
        }
    };

    const [isMobile, setIsMobile] = useState(false);
    const [showLearnerUploads, setShowLearnerUploads] = useState(false);

    const [showLearnerDownloadConfirm, setShowLearnerDownloadConfirm] =
        useState(false);

    const [selectedLearnerUpload, setSelectedLearnerUpload] =
        useState<LearnerUpload | null>(null);

    // State and handlers for Confirm Download Modal
    const [showDownloadConfirm, setShowDownloadConfirm] = useState(false);
    const [downloadId, setDownloadId] = useState<string | null>(null);
    const [selectedFiles, setSelectedFiles] = useState<Record<string, File[]>>({});
    const [showErrorModal, setShowErrorModal] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [showAssignmentModal, setShowAssignmentModal] = useState(false);
    const [assignmentCreateErrors, setAssignmentCreateErrors] = useState<{ [id: string]: string; }>({});
    const [isResourceModalOpen, setIsResourceModalOpen] = useState(false);
    const [stagedFiles, setStagedFiles] = useState<ResourceFile[]>([]);
    const [viewDate, setViewDate] = useState(new Date());
    const [showDeleteAssignmentConfirm, setShowDeleteAssignmentConfirm] = useState(false);
    const [pendingDeleteAssignmentId, setPendingDeleteAssignmentId] = useState<string | null>(null);
    const [openCalendarId, setOpenCalendarId] = useState<string | null>(null);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [pendingDeleteSubmissionId, setPendingDeleteSubmissionId] = useState<string | null>(null);
    const [showSuccessModal, setShowSuccessModal] = useState(false);
    const [pendingDeleteAssessmentId, setPendingDeleteAssessmentId] = useState<string | null>(null);
    const [showEditInfoModal, setShowEditInfoModal] = useState(false);
    const [showEditOwnerModal, setshowEditOwnerModal] = useState(false);
    const [showEditLearningsModal, setshowEditLearningsModal] = useState(false);
    const [showEditContentModal, setShowEditContentModal] = useState(false);
    const [showEditFaqModal, setShowEditFaqModal] = useState(false);
    const [coverPreview, setCoverPreview] = useState<string | undefined>(undefined);
    const [showProgramGoalsModal, setShowProgramGoalsModal] = useState(false);
    const [showEditGoalsModal, setShowEditGoalsModal] = useState(false);
    const [assignmentSuccessModal, setAssignmentSuccessModal] = useState({
        open: false,
        title: '',
        subtitle: '',
    });

    const [submissionReviewSuccess, setSubmissionReviewSuccess] = useState({
        open: false,
        title: '',
        subtitle: '',
    });

    const goalFilters = useMemo(
        () => ({
            programid: id,
            istemplate: true,
        }),
        [id]
    );

    const {
        goals,
        loading: goalsLoading,
        error: goalsError,
        fetchGoals,
        createGoal,
        updateGoal,
        deleteGoal,
    } = useGoal(goalFilters);

    const {
        goalTypes,
        loading: goalTypesLoading,
        fetchGoalTypes,
    } = useGoalType();

    const goalTypeOptions = useMemo(
        () =>
            goalTypes.map(type => ({
                value: type.name,
                label: type.name,
            })),
        [goalTypes]
    );

    const usedGoalTypeIds = useMemo(
        () =>
            goals
                .map(goal => goal.goaltypeid)
                .filter((typeId): typeId is string => Boolean(typeId)),
        [goals]
    );

    const initialUnits = [
        'Number',
        'Hours',
        'Percentage',
    ];

    const programGoals = goals.map(goal => ({
        id: goal.id,
        title: goal.title,
        targetvalue: goal.targetvalue,
        unit: goal.unit,
        description: goal.description || '',
        goaltype: goal.goaltype || '',
    }));


    const [successModal, setSuccessModal] = useState<{
        open: boolean;
        title: string;
        subtitle: string;
    }>({
        open: false,
        title: '',
        subtitle: '',
    });


    const showSuccess = (title: string, subtitle: string) => {
        setSuccessModal({
            open: true,
            title,
            subtitle,
        });

        setTimeout(() => {
            setSuccessModal(prev => ({ ...prev, open: false }));
        }, 3000);
    };
    const [uploadingResources, setUploadingResources] = useState<{
        id: string;
        name: string;
        progress: number;
        size: number;
        status: 'uploading' | 'completed' | 'failed';
    }[]>([]);
    const { updateResource, loading: updatingResource } = useResource();
    //resource edit states
    const [editingResourceId, setEditingResourceId] = useState<string | null>(null);
    const [editedResourceValues, setEditedResourceValues] = useState<{
        [key: string]: {
            visibility: boolean;
            downloadable: boolean;
        };
    }>({});
    const [activeResourceLessonFolder, setActiveResourceLessonFolder] =
        useState<{ id: string; name: string } | null>(null);
    const [activeAssignmentLessonFolder, setActiveAssignmentLessonFolder] =
        useState<{ id: string; name: string } | null>(null);
    const [editingRowId, setEditingRowId] = useState<string | null>(null);
    const [editedValues, setEditedValues] = useState<{
        [key: string]: {
            duedate: string;
            visibility: boolean;
        };
    }>({});

    const { remove } = useResourceActions();
    const { uploadResource } = useUploadResource();
    const [editingAssessment, setEditingAssessment] = useState<any | null>(null);
    const [resourceSuccessModal, setResourceSuccessModal] = useState<{
        open: boolean;
        title: string;
        subtitle: string;
    }>({
        open: false,
        title: '',
        subtitle: '',
    });


    const [showDeleteResourceConfirm, setShowDeleteResourceConfirm] = useState(false);
    const [pendingDeleteResourceId, setPendingDeleteResourceId] = useState<string | null>(null);
    const handleAddFileClick = () => { fileInputRef.current?.click(); };

    useEffect(() => {
        const checkMobile = () => setIsMobile(window.innerWidth <= 768);
        checkMobile();
        window.addEventListener('resize', checkMobile);
        return () => window.removeEventListener('resize', checkMobile);
    }, []);


    // Helper logic to build the calendar grid
    const generateDays = (viewDate: { getFullYear: () => any; getMonth: () => any; }, currentDueDate: string | number | Date) => {
        const year = viewDate.getFullYear();
        const month = viewDate.getMonth();
        const firstDayOfMonth = new Date(year, month, 1).getDay(); // 0 (Sun) to 6 (Sat)
        const daysInMonth = new Date(year, month + 1, 0).getDate();
        const daysInPrevMonth = new Date(year, month, 0).getDate();

        const grid = [];
        // 1. Padding from previous month
        for (let i = firstDayOfMonth - 1; i >= 0; i--) {
            grid.push({ num: daysInPrevMonth - i, outside: true });
        }
        // 2. Current month days
        for (let i = 1; i <= daysInMonth; i++) {
            const isSelected = currentDueDate &&
                new Date(currentDueDate).getDate() === i &&
                new Date(currentDueDate).getMonth() === month &&
                new Date(currentDueDate).getFullYear() === year;
            grid.push({ num: i, selected: isSelected, event: [5, 6].includes(i) && month === 2 }); // Mock events for March
        }
        // 3. Padding for next month to complete the 6-row grid
        while (grid.length < 42) {
            grid.push({ num: grid.length - (daysInMonth + firstDayOfMonth) + 1, outside: true });
        }
        return grid;
    };

    const handleFileChangeE = (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = e.target.files;
        if (!files || files.length === 0) return;

        const file = files[0];

        if (file.size === 0) {
            showError("File is empty. Please upload a valid file.");
            e.target.value = '';
            return;
        }


        const newFile: ResourceFile = {
            id: crypto.randomUUID(),
            name: file.name,
            file,
            visibility: true,
            downloadable: true,
        };

        setStagedFiles(prev => [...prev, newFile]);
        e.target.value = '';
    };

    const handleToggleVisibility = (id: string) => {
        setStagedFiles(prev =>
            prev.map(f => f.id === id ? { ...f, visibility: !f.visibility } : f)
        );
    };

    const handleToggleDownloadable = (id: string) => {
        setStagedFiles(prev =>
            prev.map(f => f.id === id ? { ...f, downloadable: !f.downloadable } : f)
        );
    };

    const handleDeleteFile = (id: string) => {
        // remove from staged files and also clear any upload-row bleeding into the background
        setStagedFiles(prev => prev.filter(f => f.id !== id));
        setUploadingResources(prev => prev.filter(r => r.id !== id));
    };

    const handleResourceSubmit = async () => {
        if (!course?.id || stagedFiles.length === 0) {
            showError("Please select at least one file");
            return;
        }

        const MAX_FILE_SIZE_BYTES = 1024 * 1024 * 1024;

        for (const staged of stagedFiles) {
            if (staged.file.size > MAX_FILE_SIZE_BYTES) {
                showError(`File exceeds the maximum allowed size of 1 GB`);
                return;
            }
        }

        // 🔥 Close modal immediately
        setIsResourceModalOpen(false);

        // 🔥 Create uploading entries (status = uploading)
        const tempUploads = stagedFiles.map(f => ({
            id: f.id,
            name: f.name,
            progress: 0,
            size: f.file.size,
            status: 'uploading' as const,
        }));

        setUploadingResources(prev => [...prev, ...tempUploads]);

        try {
            let allSuccess = true;

            for (const staged of stagedFiles) {
                const result = await uploadResource(
                    staged.file,
                    {
                        title: staged.name,
                        resourcetype: "document",
                        relatedcourseid: course.id,
                        relatedlessonid: activeResourceLessonFolder?.id,
                        visibility: staged.visibility ? "public" : "private",
                        candownload: staged.downloadable,
                    } as any,
                    (percent: number) => {
                        const safePercent = Math.min(percent, 99); // LIMIT TO 99
                        setUploadingResources(prev =>
                            prev.map(r =>
                                r.id === staged.id
                                    ? { ...r, progress: safePercent }
                                    : r
                            )
                        );
                    }
                );

                // update state for this file
                setUploadingResources(prev =>
                    prev.map(r =>
                        r.id === staged.id
                            ? {
                                ...r,
                                status: result.success ? 'completed' : 'failed',
                                progress: result.success ? 100 : r.progress,
                            }
                            : r
                    )
                );

                if (!result.success) {
                    allSuccess = false;
                    showError(`Failed to upload "${staged.name}": ${result.message || 'unknown error'}`);
                    // do NOT break; continue uploading other files
                } else {
                    // remove successful file from staged list
                    setStagedFiles(prev => prev.filter(f => f.id !== staged.id));
                }
            }

            if (allSuccess) {
                // Small delay so 100% is visible
                setTimeout(() => {
                    setUploadingResources([]);
                }, 500);

                await refreshResources();

                setResourceSuccessModal({
                    open: true,
                    title: "Resource Uploaded Successfully",
                    subtitle: "Your file has been uploaded to the course."
                });

                setTimeout(() => {
                    setResourceSuccessModal(prev => ({ ...prev, open: false }));
                }, 3000);
            } else {
                // leave failed rows visible, clear the completed ones after a short delay
                setTimeout(() => {
                    setUploadingResources(prev => prev.filter(r => r.status !== 'completed'));
                }, 1000);
            }
        } catch (err: any) {
            showError(err);
            setUploadingResources([]);
        }
    };


    const handleMarkComplete = async (lessonId: string, isCompleted: boolean) => {
        try {
            await updateLesson(lessonId, {
                islive: isCompleted,
                // if completed → true (make it active again)
                // if not completed → false (mark complete)
            });

            await fetchCourseModules(id!);
            await refreshDetails();

            showSuccess(
                isCompleted ? "Marked as incomplete" : "Marked as complete",
                isCompleted
                    ? "Activity is now active again."
                    : "Activity has been completed."
            );

        } catch (err) {
            showError(err);
        }
    };

    const { create, loading: createLoading } = useCreateAssessment();

    const { profile, loading: profileLoading } = useUserProfile();



    const fileInputRef = useRef<HTMLInputElement>(null);
    const showError = (error: any) => {
        let message = 'Something went wrong';

        // ✅ Backend message (priority)
        if (error?.response?.data?.message) {
            message = error.response.data.message.replace(/"/g, '');
        }
        // ✅ Sometimes API returns string directly
        else if (typeof error?.response?.data === 'string') {
            message = error.response.data;
        }
        // ✅ Custom errors (your own throws)
        else if (typeof error === 'string') {
            message = error;
        }
        // ✅ Axios fallback
        else if (error?.message) {
            message = error.message;
        }

        setErrorMessage(message);
        setShowErrorModal(true);
    };
    const handleUploadClick = () => {
        // Trigger the hidden input click
        fileInputRef.current?.click();
    };

    const handleFileChange = (
        assessmentId: string,
        event: React.ChangeEvent<HTMLInputElement>
    ) => {
        if (!event.target.files) return;

        setSelectedFiles(prev => ({
            ...prev,
            [assessmentId]: Array.from(event.target.files!),
        }));
    };

    const {
        assessments,
        loading: assessmentsLoading,
        error: assessmentsError,
        updateAssessment,
        deleteAssessment,
        refetch: refetchAssessments,
    } = useCourseAssessments(id);

    const {
        submit,
        submitting,
        progress,
        error: submitError,
        success,
    } = useSubmitAssessment();

    const resetUploadProgress = (assessmentId: string) => {
        setUploadProgress(prev => {
            const copy = { ...prev };
            delete copy[assessmentId];
            return copy;
        });
    };

    const confirmDeleteAssignment = async () => {
        if (!pendingDeleteAssignmentId) return;

        const backendId = assessments.find(
            a => a.id.toString() === pendingDeleteAssignmentId
        )?.id;

        if (!backendId) return;

        try {
            const success = await deleteAssessment(backendId);

            if (success) {
                await refetchAssessments();
                setShowDeleteAssignmentConfirm(false);
                setPendingDeleteAssignmentId(null);

                // 🔥 SHOW SUCCESS MODAL
                setAssignmentSuccessModal({
                    open: true,
                    title: 'Assignment Deleted Successfully',
                    subtitle: 'The assignment has been removed from the program.',
                });

                setTimeout(() => {
                    setAssignmentSuccessModal(prev => ({ ...prev, open: false }));
                }, 3000);
            }
            setShowDeleteAssignmentConfirm(false);
        } catch (err) {

            showError(err);

            console.error('Delete assignment failed', err);
        }
    };
    const handleAutoUpload = async (
        event: React.ChangeEvent<HTMLInputElement>,
        assessment: any
    ) => {
        if (!event.target.files?.length) return;

        const files = event.target.files;

        if (!files || files.length === 0) {
            showError(
                'There is no content in this file. Please select appropriate file.'
            );
            return;
        }

        // const selectedFiles = Array.from(files);
        const selectedFiles = Array.from(event.target.files);

        try {
            // 🔒 CLIENT-SIDE FILE TYPE CHECK
            const allowedTypes = (assessment.allowedfiletypes || []).map(
                (t: string) => t.toLowerCase()
            );

            for (const file of selectedFiles) {


                if (file.size === 0) {
                    showError("One of the selected files is empty. Please upload valid content.");
                    event.target.value = '';
                    return;
                }
                const ext = file.name.split('.').pop()?.toLowerCase();

                if (!ext || !allowedTypes.includes(ext)) {
                    showError(
                        `File type not allowed. Allowed types: ${allowedTypes.join(', ')}`
                    );
                    event.target.value = '';
                    return; // 🚫 STOP upload
                }
            }

            // 🔒 CLIENT-SIDE STATIC FILE SIZE CHECK (1 GB MAX)
            const MAX_FILE_SIZE_BYTES = 1024 * 1024 * 1024; // 1 GB

            for (const file of selectedFiles) {
                if (file.size > MAX_FILE_SIZE_BYTES) {
                    showError(
                        `File size exceeded. Maximum allowed size is 1 GB`
                    );
                    event.target.value = '';
                    return; // 🚫 STOP upload
                }
            }

            const response = await submit(
                assessment.id,
                {
                    files: selectedFiles,
                    answers: {}, // future-proof (quiz/text answers)
                },
                percent => {
                    setUploadProgress(prev => ({
                        ...prev,
                        [assessment.id]: Math.min(percent, 99),
                    }));
                }
            );

            if (!response || response.success === false) {
                throw response;
            }

            // ✅ INSTANT UI UPDATE (NO REFRESH)

            updateAssessmentStatusLocally(assessment.id, {
                has_submitted: true,
            });

            // ✅ SHOW SUCCESS MODAL
            setShowSuccessModal(true);
            setUploadProgress(prev => ({
                ...prev,
                [assessment.id]: 100,
            }));
            resetUploadProgress(assessment.id);
            await refetchAssessmentStatuses();
            // auto-close modal
            setTimeout(() => {
                setShowSuccessModal(false);
            }, 3000);
            // reset progress after success

            // Clear input so same file can be re-selected if needed
            event.target.value = '';
        } catch (err: any) {
            showError(err);

            resetUploadProgress(assessment.id);

            event.target.value = '';
        }
    };

    const {
        statuses: assessmentStatuses,
        loading: statusLoading,
        setStatuses: setAssessmentStatuses,
        refetch: refetchAssessmentStatuses,
    } = useCourseAssessmentStatuses(id);

    const updateAssessmentStatusLocally = (
        assessmentId: string,
        data: Partial<AssessmentStatus>
    ) => {
        console.log(
            '[OPTIMISTIC UPDATE]',
            'assessmentId:',
            assessmentId,
            'data:',
            data
        );
        setAssessmentStatuses(prev => {
            console.log('[BEFORE STATE]', prev[assessmentId]);

            const next = {
                ...prev,
                [assessmentId]: {
                    ...prev[assessmentId],
                    ...data,
                },
            };

            console.log('[AFTER STATE]', next[assessmentId]);

            return next;
        });
    };


    const {
        modules,
        lessons,
        fetchCourseModules,
        fetchModuleLessons,
        createModule,
        updateModule,
        deleteModule,
        createLesson,
        updateLesson,
        deleteLesson,
    } = useCourseModules();

    useEffect(() => {
        console.log(
            'STEP 1 - modules from hook',
            modules
        );
    }, [modules]);



    const { resources, refetch: refreshResources } = useResources(id ? 'course' : 'all', id || '');

    const lessonFolders = React.useMemo(() => {
        return modules.flatMap(module =>
            (module.lessons || []).map(lesson => ({
                id: lesson.id,
                name: lesson.title,
                isFolder: true,
                isActive: true,           // ✅ REQUIRED
                visibility: true,
                downloadable: false,
            }))
        );
    }, [modules]);

    const rootFiles = resources
        .filter(res => !res.relatedlessonid)
        .map(res => ({
            id: res.id,
            name: res.title,
            size: formatFileSize(res.filesize),
            isFolder: false,
            raw: res,
            visibility: res.visibility === 'public',
            downloadable: res.candownload,
        }));

    const lessonFiles = activeResourceLessonFolder
        ? resources
            .filter(res => res.relatedlessonid === activeResourceLessonFolder.id)
            .map(res => ({
                id: res.id,
                name: res.title,
                size: formatFileSize(res.filesize),
                isFolder: false,
                raw: res,
                visibility: res.visibility === 'public',
                downloadable: res.candownload,
            }))
        : [];
    const rootAssignments = assessments
        .filter(a => !a.lessonid)
        .map(a => ({
            id: a.id.toString(),
            backendId: a.id,
            name: a.title.charAt(0).toUpperCase() + a.title.slice(1),
            type: getFileTypeLabel(a.allowedfiletypes) || '',
            dueDate: a.duedate
                ? new Date(a.duedate).toISOString().split('T')[0]
                : '',

            isFolder: false,
            isActive: a.allowfilesubmission,
        }));

    const lessonAssignments = activeAssignmentLessonFolder
        ? assessments
            .filter(a => a.lessonid === activeAssignmentLessonFolder.id)
            .map(a => ({
                id: a.id.toString(),
                backendId: a.id,
                name: a.title.charAt(0).toUpperCase() + a.title.slice(1),
                type: getFileTypeLabel(a.allowedfiletypes) || '',
                dueDate: a.duedate
                    ? new Date(a.duedate).toISOString().split('T')[0]
                    : '',

                isFolder: false,
                isActive: a.allowfilesubmission,
            }))
        : [];

    const displayedAssignmentData = activeAssignmentLessonFolder
        ? lessonAssignments
        : [...lessonFolders, ...rootAssignments];

    const uploadingRows: ResourceItem[] = uploadingResources.map(r => ({
        id: r.id,
        name: r.name,
        isFolder: false,
        status: r.status, // already correct union type
        // 👇 Only mark as uploading on MOBILE
        isUploading: isMobile ? r.status === 'uploading' : false,
        progress: r.progress,
        size: formatFileSize(r.size),
    } as ResourceItem));

    const displayedResourceData = activeResourceLessonFolder
        ? lessonFiles
        : [...lessonFolders, ...uploadingRows, ...rootFiles];

    const { deleteSubmission, deleting: deletingSubmission } = useDeleteAssessmentSubmission();

    const confirmDeleteSubmission = async () => {
        console.log('[DELETE CONFIRM] clicked');
        if (!pendingDeleteSubmissionId || !pendingDeleteAssessmentId) {
            console.log('[DELETE CONFIRM] missing ids', {
                pendingDeleteSubmissionId,
                pendingDeleteAssessmentId,
            });
            return;
        }
        console.log('[DELETE CONFIRM] ids OK', {
            submissionId: pendingDeleteSubmissionId,
            assessmentId: pendingDeleteAssessmentId,
        });
        try {
            updateAssessmentStatusLocally(pendingDeleteAssessmentId, {
                has_submitted: false,
                submission_id: undefined,
            });
            console.log('[DELETE CONFIRM] optimistic update done');
            await deleteSubmission(pendingDeleteSubmissionId);
            console.log('[DELETE CONFIRM] backend delete success');
            // refresh statuses
            await refetchAssessmentStatuses();
            console.log('[DELETE CONFIRM] refetch done');
            setShowDeleteConfirm(false);
            setPendingDeleteSubmissionId(null);
        } catch (e) {
            console.error('[DELETE CONFIRM] error', e);
            setShowDeleteConfirm(false);
            setPendingDeleteSubmissionId(null);
        }
    };

    const cancelDeleteSubmission = () => {
        setShowDeleteConfirm(false);
        setPendingDeleteSubmissionId(null);
    };

    const handleDeleteSubmission = (
        submissionId: string,
        assessmentId: string
    ) => {
        setPendingDeleteSubmissionId(submissionId);
        setPendingDeleteAssessmentId(assessmentId);
        setShowDeleteConfirm(true);
    };

    const handleAddFolder = (type: string) => {
        console.log('Add folder for', type);
    };

    const handleFileUpload = (type: string) => {
        console.log('File upload for', type);
    };

    const confirmDeleteResource = async () => {
        if (!pendingDeleteResourceId) return;

        try {
            const success = await remove(pendingDeleteResourceId);

            if (success) {
                setShowDeleteResourceConfirm(false);
                setPendingDeleteResourceId(null);

                // Optional: show success modal
                setResourceSuccessModal({
                    open: true,
                    title: 'Resource Deleted Successfully',
                    subtitle: 'The resource has been removed from the course.',
                });

                setTimeout(() => {
                    setResourceSuccessModal(prev => ({ ...prev, open: false }));
                }, 3000);
            }
            await refreshResources();
        } catch (err) {
            console.error('Delete failed', err);
        }
    };

    const cancelDeleteResource = () => {
        setShowDeleteResourceConfirm(false);
        setPendingDeleteResourceId(null);
    };



    const dueAssignmentsCount = React.useMemo(() => {
        if (!assessments?.length) {
            console.log('📊 Count Debug: No assessments found yet.');
            return 0;
        }

        const count = assessments.filter(assessment => {
            const status = assessmentStatuses[assessment.id];
            const isSubmitted = status?.has_submitted === true;

            console.log(`🔍 Checking Assessment [${assessment.title}]:`, {
                id: assessment.id,
                statusFound: !!status,
                isSubmitted: isSubmitted,
            });

            return !isSubmitted; // We want to count ones NOT submitted
        }).length;

        console.log('✅ Final Due Count:', count);
        return count;
    }, [assessments, assessmentStatuses]);

    // const getCoverUrl = () =>
    //     `http://localhost:5000/api/course/${course?.id}/cover?t=${Date.now()}`;

    const navigate = useNavigate();
    const FAQs_PER_PAGE = 5;
    const [faqPage, setFaqPage] = useState(1);

    const { course, isLoading, error, refreshDetails } = useCourseDetails(id || '');

    console.log("📦 COURSE MODULES 👉", course?.modules);

    const { updateCourse } = useUpdateCourse();
    // const { coaches, loading: coachesLoading } = useCoachesList('coach');
    const { download, isProcessing } = useResourceActions();
    // Add this state to track which Assessment is being modified

    const formattedModules = (modules || []).map((module: any, index: number) => {
        const lessons = (module.lessons || []).map(
            (lesson: {
                id: any;
                title: any;
                scheduledstarttime: any;
                scheduledendtime: any;
                activitytype?: {
                    id: string;
                    title: string;
                    color?: string;
                    icon?: string | null;
                };
                metadata: any;
                duration: any;
                islive?: boolean;
                description?: string;
            }, i: number) => ({
                id: lesson.id,
                sequence: i + 1,
                title: lesson.title,
                date: formatModuleDate(lesson.scheduledstarttime || ''),
                dateTimeStr: formatActivityDateTime(
                    lesson.scheduledstarttime,
                    lesson.scheduledendtime
                ),
                type: lesson.activitytype?.title || 'Session',
                typeColor: lesson.activitytype?.color || '#F97316',
                participants: lesson.metadata ? ['User1', 'User2'] : [],
                isCompleted: lesson.islive === false,
                duration: lesson.duration || 0,
                description: lesson.description || '',
            })
        );

        console.log("⚙️ Mapping module 👉", module);

        return {
            id: module.id,
            sequence: index + 1,
            title: module.title || '',


            startdate: module.startdate || '',
            enddate: module.enddate || '',

            description: module.description || '',
            isCompleted: false,
            lessons,
            totalActivities: lessons.length,
        };
    });

    console.log("✅ formattedModules 👉", formattedModules);

    useEffect(() => {
        if (id) {
            fetchCourseModules(id);
        }
    }, [id, fetchCourseModules]);

    const {
        createFAQ,
        updateFAQ,
        isLoading: faqActionLoading
    } = useFAQActions();

    const {
        articles: faqs,
        isLoading: isFaqLoading,
        error: faqError,
        refreshArticles
    } = useFAQArticles({
        category: course?.category,
        // ispublished: true,
    });

    // State for Accordion
    const [activeSection, setActiveSection] = useState<number | null>(0);
    const [activeFaq, setActiveFaq] = useState<number | null>(null);
    const [uploadProgress, setUploadProgress] = useState<
        Record<string, number>
    >({});

    const totalFaqPages = Math.ceil(faqs.length / FAQs_PER_PAGE);

    const paginatedFaqs = faqs.slice(
        (faqPage - 1) * FAQs_PER_PAGE,
        faqPage * FAQs_PER_PAGE
    );

    useEffect(() => {
        if (assessmentsError) {
            showError(assessmentsError);
        }
    }, [assessmentsError]);

    useEffect(() => {
        if (submitError) {
            showError(submitError);
        }
    }, [submitError]);

    const mappedFaqs = faqs.map(faq => ({
        query: faq.title,
        response: faq.content
    }));

    const initialModulesData = modules.map((module, index) => {

        // Map existing lessons
        const mappedActivities = (module.lessons || []).map((lesson) => ({
            id: lesson.id,
            name: lesson.title || `Activity ${lesson.id}`,
        }));

        // Map existing assessments
        const moduleAssessments = assessments
            .filter(a => a.lessonid && module.lessons?.some(l => l.id === a.lessonid))
            .map(assessment => ({
                id: assessment.id,
                name: assessment.title || 'Assessment',
            }));

        // Combine them
        const allActivities = [...mappedActivities, ...moduleAssessments];

        return {
            id: module.id,
            name: module.title || '',
            startDate: module.startdate || '',
            endDate: module.enddate || '',
            description: module.description || '',
            activities: allActivities,
        };
    });

    const validateFaqs = (faqs: { query: string; response: string }[]) => {
        for (let i = 0; i < faqs.length; i++) {
            const faq = faqs[i];

            const title = faq.query?.trim();
            const response = faq.response?.trim();

            // ❌ Title validation
            if (!title || title.length < 5) {
                throw new Error(`FAQ ${i + 1}: Title must be at least 5 characters long`);
            }

            // ❌ Response empty
            if (!response) {
                throw new Error(`FAQ ${i + 1}: Description cannot be empty`);
            }

            // ❌ Response length
            if (response.length < 20) {
                throw new Error(`FAQ ${i + 1}: Description must be at least 20 characters long`);
            }
        }
    };



    useEffect(() => {
        const fetchCover = async () => {
            try {
                const res = await apiService.get<{ picture: string }>(`course/${course?.id}/cover`);
                setCoverPreview(res?.data?.picture); // base64
            } catch (err) {
                console.error("Cover fetch failed");
            }
        };

        if (course?.id) fetchCover();
    }, [course?.id]);

    const handleFaqSave = async (
        updatedFaqs: { query: string; response: string }[]
    ) => {
        try {

            validateFaqs(updatedFaqs);

            let promises: Promise<any>[] = [];

            // 🔥 CASE 1: NO FAQ → CREATE
            if (!faqs || faqs.length === 0) {
                promises = updatedFaqs.map(faq =>
                    createFAQ({
                        title: faq.query,
                        content: faq.response,
                        category: course?.category,
                    })
                );
            } else {
                // 🔥 CASE 2: UPDATE EXISTING
                promises = updatedFaqs.map((faq, index) => {
                    const existingFaq = faqs[index];

                    // If exists → update
                    if (existingFaq?.id) {
                        return updateFAQ(existingFaq.id, {
                            title: faq.query,
                            content: faq.response,
                        });
                    }

                    // If extra new FAQ added → create
                    return createFAQ({
                        title: faq.query,
                        content: faq.response,
                        category: course?.category,
                    });
                });
            }

            const results = await Promise.all(promises);

            const failed = results.find(r => !r?.success);

            if (failed) {
                showError(
                    failed?.message ||
                    "Some FAQs failed to save"
                );
                return; // ❌ STOP SUCCESS POPUP
            }

            // ✅ REFRESH FAQs AFTER SAVE
            await refreshArticles();

            showSuccess(
                faqs.length === 0
                    ? 'FAQs Created Successfully'
                    : 'FAQs Updated Successfully',
                'All FAQ changes have been saved.'
            );

            setShowEditFaqModal(false);
        } catch (err: any) {

            showError(err.message || err);
            console.error(err);
        }
    };

    const isAdminView =
        profile?.rolename === 'admin' ||
        profile?.rolename === 'superadmin' ||
        profile?.id === course?.programownerid;
    if (isLoading) return <div className={styles.loading}>Loading...</div>;
    if (error || !course)
        return <div className={styles.error}>Error: {error}</div>;


    return (

        <div className={styles.container}>

            <input
                type="file"
                ref={fileInputRef}
                style={{ display: 'none' }}
                onChange={handleFileChangeE}
            />


            {/* --- Top Navigation --- */}
            <div className={styles.navHeader}>
                <button className={styles.backBtn} onClick={() => navigate(-1)}>
                    <ArrowLeft size={20} /> Back
                </button>
            </div>

            {/* --- Main Info Section --- */}
            <div className={styles.mainGrid}>
                <div className={styles.leftCol}>
                    <nav className={styles.breadcrumbs}>
                        {course.sol_title}{' '}
                        <span>
                            {' '}
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="8"
                                height="14"
                                viewBox="0 0 8 14"
                                fill="none"
                            >
                                <path
                                    d="M0.75 12.75L6.75 6.75L0.75 0.75"
                                    stroke="#949494"
                                    stroke-width="1.5"
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                />
                            </svg>{' '}
                        </span>{' '}
                        {course.title.toUpperCase()}
                    </nav>

                    <EditableSection onEdit={() => setShowEditInfoModal(true)}>
                        <h1 className={styles.mainTitle}>{course.title}</h1>
                    </EditableSection>
                    <section className={styles.section2}>
                        <h3 className={styles.sectionTitle}>Description</h3>
                        <p className={styles.descriptionText}>
                            {course.description}
                        </p>
                    </section>

                    <div className={styles.line}></div>
                    {/* --- Program Includes --- */}
                    {course.programincludes &&
                        course.programincludes && (
                            <section className={styles.section}>
                                <EditableSection onEdit={() => setshowEditLearningsModal(true)}>
                                    <div className={styles.sectionHeaderLine}>

                                        <h3 className={styles.sectionTitle}>
                                            Program Includes:
                                        </h3>


                                    </div>
                                </EditableSection>

                                <ul className={styles.includesList}>
                                    {course.programincludes.map(
                                        (item: string, index: number) => (
                                            <li key={index}>{item}</li>
                                        )
                                    )}
                                </ul>
                            </section>
                        )}
                </div>

                <div className={styles.rightCol}>
                    <EditableSection onEdit={() => setshowEditOwnerModal(true)}>
                        <div className={styles.stickyCard}>
                            <img
                                src={coverPreview}
                                alt="Course"
                                className={styles.featuredImg}
                            />
                            <div className={styles.metaTable}>
                                {/* Program Owner Section */}
                                <div className={styles.metaInfoRow}>
                                    <div className={styles.metaIcon}>
                                        <ProgramOwner />
                                    </div>
                                    <div className={styles.metaText}>
                                        <span className={styles.metaLabel}>
                                            Program Owner & Lead Faculty:
                                        </span>
                                        <strong className={styles.metaValue}>
                                            {course.programownername ||
                                                'Not Specified'}
                                        </strong>
                                    </div>
                                </div>

                                {/* Program Format Section */}
                                <div className={styles.metaInfoRow}>
                                    <div className={styles.metaIcon}>
                                        <ProgramFormat />
                                    </div>
                                    <div className={styles.metaText}>
                                        <span className={styles.metaLabel}>
                                            Program Format:
                                        </span>
                                        <strong className={styles.metaValue}>
                                            {course.programformat || 'Not Specified'}
                                        </strong>
                                    </div>
                                </div>

                                {/* Certification Section */}
                                <div className={styles.metaInfoRow}>
                                    <div className={styles.metaIcon}>
                                        <ProgramCertification />
                                    </div>
                                    <div className={styles.metaText}>
                                        <span className={styles.metaLabel}>
                                            Certification:
                                        </span>
                                        <strong className={styles.metaValue}>
                                            {course.certification ||
                                                'Not specified'}
                                        </strong>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </EditableSection>
                </div>
            </div>

            {/* --- What You'll Learn --- */}

            {course.learningtakeaways &&
                course.learningtakeaways && (
                    <div className={styles.fullWidthSectionLearn}>
                        <EditableSection onEdit={() => setshowEditLearningsModal(true)}>
                            <div className={styles.sectionHeaderLine}>
                                <h3 className={styles.sectionTitle}>
                                    What you'll learn
                                </h3>
                                <div className={styles.line}></div>
                            </div>
                        </EditableSection>
                        <div className={styles.learningGrid}>
                            {course.learningtakeaways.map(
                                (point: string, i: number) => (
                                    <div
                                        key={i}
                                        className={styles.learningItem}
                                    >
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            width="27"
                                            height="27"
                                            viewBox="0 0 27 27"
                                            fill="none"
                                        >
                                            <path
                                                d="M25.4983 10.7542C26.0692 13.5559 25.6623 16.4685 24.3456 19.0065C23.0289 21.5444 20.8819 23.5543 18.2627 24.7009C15.6435 25.8475 12.7103 26.0615 9.95244 25.3072C7.19453 24.5529 4.77856 22.876 3.10742 20.556C1.43627 18.236 0.610975 15.4133 0.769153 12.5585C0.92733 9.70367 2.05942 6.98937 3.97663 4.86822C5.89385 2.74707 8.48029 1.3473 11.3046 0.902326C14.129 0.457355 17.0205 0.994084 19.4971 2.42301M9.49706 12.0042L13.2471 15.7542L25.747 3.25426"
                                                stroke="#147B58"
                                                strokeWidth="1.5"
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                            />
                                        </svg>

                                        <p>{point}</p>
                                    </div>
                                )
                            )}
                        </div>
                    </div>
                )}

            {/* --- Program Goals --- */}

            {programGoals?.length > 0 ? (

                <div className={styles.fullWidthSectionLearn}>
                    <EditableSection
                        onEdit={() => setShowEditGoalsModal(true)}
                    >
                        <ProgramGoals
                            title="Program Goals"
                            goals={programGoals}
                        />
                    </EditableSection>
                </div>

            ) : (

                <div className={goalStyles.emptyContainer}>
                    <h2 className={goalStyles.emptyTitle}>
                        Program Goals
                    </h2>

                    <button
                        className={goalStyles.addGoalsButton}
                        onClick={() => {
                            console.log('Open Add Goals Modal');
                            setShowProgramGoalsModal(true);
                        }}
                    >
                        Add Goals
                    </button>
                </div>

            )}
            {/* --- Program Content --- */}
            <div className={styles.fullWidthSection}>
                <EditableSection
                    onEdit={
                        formattedModules.length > 0
                            ? () => setShowEditContentModal(true)
                            : undefined
                    }
                >
                    <div className={styles.sectionHeaderLine}>
                        {formattedModules.length === 0 && (
                            <h3 className={styles.sectionTitle}>
                                Program Content
                            </h3>
                        )}
                        {formattedModules.length === 0 && (
                            <button
                                type="button"
                                className={styles.enrollBtn}
                                onClick={() => setShowEditContentModal(true)}
                            >
                                Add Content
                            </button>
                        )}
                    </div>



                    <ProgramContent
                        modules={formattedModules}
                        onMarkComplete={handleMarkComplete}
                        showMarkCompleteButton={true}
                    // loading={modulesLoading}

                    />
                    {/* ✅ CONDITIONAL CONTENT */}
                    <ResponsiveModal
                        open={showEditContentModal}
                        onClose={() => setShowEditContentModal(false)}
                    >
                        <ProgramContentBuilder
                            courseId={course.id}
                            modules={modules}
                            fetchCourseModules={fetchCourseModules}
                            fetchModuleLessons={fetchModuleLessons}
                            createModule={createModule}
                            updateModule={updateModule}
                            deleteModule={deleteModule}
                            createLesson={createLesson}
                            updateLesson={updateLesson}
                            deleteLesson={deleteLesson}
                            onClose={() => setShowEditContentModal(false)}
                            onSave={async () => {
                                await fetchCourseModules(course.id);   // 🔥 THIS IS KEY
                                await refreshDetails();
                                showSuccess(
                                    'Program Content Saved',
                                    'Your modules and activities have been updated successfully.'
                                );
                            }}
                            onError={showError}
                        />
                    </ResponsiveModal>
                </EditableSection>
            </div>
            <div className={styles.fullWidthSectionResourcesUpload}>
                {/* --- Resources Section --- */}
                {/* --- RESOURCES SECTION --- */}
                <div className={styles.fullWidthSectionRU}>

                    <ResourceManager
                        title="Program Resources"
                        editingRowId={editingResourceId}
                        breadcrumb={
                            activeResourceLessonFolder
                                ? [course.title, activeResourceLessonFolder.name]
                                : [course.title]
                        }
                        primaryActionText="Add Resource File"
                        data={displayedResourceData}
                        onRowClick={(item) => {
                            if (item.isFolder) {
                                setActiveResourceLessonFolder({
                                    id: item.id,
                                    name: item.name
                                });
                            }
                        }}
                        onBack={
                            activeResourceLessonFolder
                                ? () => setActiveResourceLessonFolder(null)
                                : undefined
                        }
                        onAddFolder={() => handleAddFolder('resource')}
                        onAddResource={() => setIsResourceModalOpen(true)}
                        onDelete={(id) => {
                            setPendingDeleteResourceId(id);
                            setShowDeleteResourceConfirm(true);
                        }}

                        onEdit={async (id) => {

                            if (editingResourceId === id) {
                                // ✅ SAVE MODE

                                const values = editedResourceValues[id];
                                if (!values) return;

                                try {
                                    const response = await updateResource(id, {
                                        visibility: values.visibility ? 'public' : 'private',
                                        candownload: values.downloadable,
                                    });

                                    if (!response.success) {
                                        showError(response.message);
                                        return;
                                    }

                                    await refreshResources();

                                    setEditingResourceId(null);
                                    setEditedResourceValues({});

                                    setResourceSuccessModal({
                                        open: true,
                                        title: 'Resource Updated Successfully',
                                        subtitle: 'Resource settings have been updated.',
                                    });

                                    setTimeout(() => {
                                        setResourceSuccessModal(prev => ({ ...prev, open: false }));
                                    }, 3000);

                                } catch (err: any) {
                                    showError(err);
                                }

                            } else {
                                // ✅ EDIT MODE

                                const current = resources.find(r => r.id === id);
                                if (!current) return;

                                setEditingResourceId(id);

                                setEditedResourceValues(prev => ({
                                    ...prev,
                                    [id]: {
                                        visibility: current.visibility === 'public',
                                        downloadable: current.candownload,
                                    },
                                }));
                            }
                        }}
                        onRemoveLocal={(id: string) => {
                            // remove the file from staged list and any in-progress uploads
                            setStagedFiles(prev => prev.filter(f => f.id !== id));
                            setUploadingResources(prev => prev.filter(r => r.id !== id));
                        }}
                        columns={[
                            {
                                header: 'Size',
                                render: (item) => {
                                    if (item.isFolder) return null;

                                    // 🔥 Show progress bar
                                    if (!isMobile && uploadingResources.find(r => r.id === item.id)) {
                                        const upload = uploadingResources.find(r => r.id === item.id);
                                        return (
                                            <div style={{ width: '120px' }}>
                                                <div
                                                    style={{
                                                        height: '6px',
                                                        background: '#eee',
                                                        borderRadius: '4px',
                                                        overflow: 'hidden'
                                                    }}
                                                >
                                                    <div
                                                        style={{
                                                            width: `${item.progress || 0}%`,
                                                            height: '100%',
                                                            background: '#147B58',
                                                            transition: 'width 0.3s ease'
                                                        }}
                                                    />
                                                </div>
                                                <div style={{ fontSize: '12px', marginTop: '4px' }}>
                                                    {item.progress || 0}%
                                                </div>
                                            </div>
                                        );
                                    }

                                    // 🔥 Normal size display
                                    return <span>{item.size || '--'}</span>;
                                }
                            },


                            {
                                header: 'Visibility',
                                render: (item) => {
                                    if (item.isFolder) return null;

                                    const isEditing = editingResourceId === item.id;

                                    return (
                                        <input
                                            type="checkbox"
                                            checked={
                                                isEditing
                                                    ? editedResourceValues[item.id]?.visibility ?? item.visibility
                                                    : item.visibility
                                            }
                                            disabled={!isEditing}
                                            onChange={(e) =>
                                                setEditedResourceValues(prev => ({
                                                    ...prev,
                                                    [item.id]: {
                                                        ...prev[item.id],
                                                        visibility: e.target.checked,
                                                        downloadable:
                                                            prev[item.id]?.downloadable ?? item.downloadable,
                                                    },
                                                }))
                                            }
                                        />
                                    );
                                },
                            },
                            {
                                header: 'Downloadable',
                                render: (item) => {
                                    if (item.isFolder) return null;

                                    const isEditing = editingResourceId === item.id;

                                    return (
                                        <input
                                            type="checkbox"
                                            checked={
                                                isEditing
                                                    ? editedResourceValues[item.id]?.downloadable ?? item.downloadable
                                                    : item.downloadable
                                            }
                                            disabled={!isEditing}
                                            onChange={(e) =>
                                                setEditedResourceValues(prev => ({
                                                    ...prev,
                                                    [item.id]: {
                                                        ...prev[item.id],
                                                        downloadable: e.target.checked,
                                                        visibility:
                                                            prev[item.id]?.visibility ?? item.visibility,
                                                    },
                                                }))
                                            }
                                        />
                                    );
                                },
                            },
                        ]}
                    />
                </div>
            </div>

            {/* --- Upload Assignment Section --- */}
            {/* --- ASSESSMENTS SECTION --- */}
            <div className={styles.fullWidthSectionRU}>

                <ResourceManager
                    title="Learner Assignment"
                    editingRowId={editingRowId}
                    breadcrumb={
                        activeAssignmentLessonFolder
                            ? [course.title, activeAssignmentLessonFolder.name]
                            : [course.title]
                    }
                    primaryActionText="Create New Assignment"
                    secondaryActionText="View Learner Uploads"
                    onSecondaryAction={handleViewLearnerUploads}
                    data={displayedAssignmentData}
                    onRowClick={(item) => {
                        if (item.isFolder) {
                            setActiveAssignmentLessonFolder({
                                id: item.id,
                                name: item.name
                            });
                        }
                    }}
                    onBack={
                        activeAssignmentLessonFolder
                            ? () => setActiveAssignmentLessonFolder(null)
                            : undefined
                    }
                    onAddFolder={() => handleAddFolder('resource')}
                    onAddResource={() => {
                        setShowAssignmentModal(true);
                    }}

                    onEdit={async (id) => {
                        if (editingRowId === id) {
                            // SAVE MODE
                            const values = editedValues[id];
                            if (!values) return;

                            const backendId = assessments.find(a => a.id.toString() === id)?.id;

                            if (!backendId) {
                                showError("Assessment ID not found. Unable to save changes.");
                                console.error("Assessment ID not found");
                                return;
                            }

                            try {
                                await updateAssessment(backendId, {
                                    duedate: new Date(values.duedate).toISOString(),
                                    allowfilesubmission: values.visibility,
                                    // lessonid: activeAssignmentLessonFolder?.id,
                                });
                                await refetchAssessments();
                            } catch (err) {
                                showError(err);
                            }

                            setEditingRowId(null);
                            setEditedValues({});
                            setResourceSuccessModal({
                                open: true,
                                title: 'Assignment Updated Successfully',
                                subtitle: 'Assignment settings have been updated.',
                            });

                            setTimeout(() => {
                                setResourceSuccessModal(prev => ({ ...prev, open: false }));
                            }, 3000);
                        } else {
                            // EDIT MODE
                            const current = assessments.find(
                                (a) => a.id.toString() === id
                            );

                            if (!current) return;

                            setEditingRowId(id);

                            setEditedValues((prev) => ({
                                ...prev,
                                [id]: {
                                    duedate: current.duedate
                                        ? current.duedate.toString().split('T')[0]
                                        : '',
                                    visibility: prev[id]?.visibility ?? current.allowfilesubmission ?? false,

                                },
                            }));
                        }
                    }}

                    onDelete={(id) => {
                        setPendingDeleteAssignmentId(id);
                        setShowDeleteAssignmentConfirm(true);
                    }}

                    columns={[
                        {
                            header: 'File Type',
                            render: (item) =>
                                item.isFolder ? null : <span>{item.type || '--'}</span>,
                        },

                        {
                            header: 'Visibility',
                            render: (item) => {
                                if (item.isFolder) return null; // ✅ Prevent folder rendering

                                const isEditing = editingRowId === item.id;

                                return (
                                    <input
                                        type="checkbox"
                                        checked={
                                            isEditing
                                                ? editedValues[item.id]?.visibility ?? item.isActive
                                                : item.isActive
                                        }
                                        disabled={!isEditing}
                                        onChange={(e) =>
                                            setEditedValues((prev) => ({
                                                ...prev,
                                                [item.id]: {
                                                    ...prev[item.id],
                                                    duedate:
                                                        prev[item.id]?.duedate ?? item.dueDate ?? '',
                                                    visibility: e.target.checked,
                                                },
                                            }))
                                        }
                                    />
                                );
                            },
                        },

                        {
                            header: 'Due Date',
                            render: (item) => {
                                if (item.isFolder) return null;

                                const isEditing = editingRowId === item.id;

                                const rawValue = isEditing
                                    ? editedValues[item.id]?.duedate ?? item.dueDate ?? ''
                                    : item.dueDate ?? '';

                                return (
                                    <div style={{ position: 'relative' }}>
                                        <div
                                            className={`${styles.dateContainer} ${!isEditing ? styles.disabled : ''}`}
                                            style={{ position: 'relative', cursor: isEditing ? 'pointer' : 'default' }}
                                            onClick={() => {
                                                if (isEditing) {
                                                    const input = document.getElementById(`date-${item.id}`) as HTMLInputElement;
                                                    input?.showPicker?.(); // 🔥 force open
                                                    input?.focus();
                                                }
                                            }}
                                        >
                                            <svg className={styles.dateIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                                                <line x1="16" y1="2" x2="16" y2="6" />
                                                <line x1="8" y1="2" x2="8" y2="6" />
                                                <line x1="3" y1="10" x2="21" y2="10" />
                                            </svg>

                                            <span className={styles.dateInput}>
                                                {rawValue
                                                    ? new Date(rawValue).toLocaleDateString('en-GB', {
                                                        day: '2-digit',
                                                        month: 'short',
                                                        year: '2-digit',
                                                    })
                                                    : 'Select Date'}
                                            </span>

                                            {isEditing && (
                                                <input
                                                    id={`date-${item.id}`}   // 👈 important
                                                    type="date"
                                                    value={rawValue}
                                                    min={new Date().toISOString().split('T')[0]}
                                                    onChange={(e) => {
                                                        const iso = e.target.value;

                                                        setEditedValues((prev) => ({
                                                            ...prev,
                                                            [item.id]: {
                                                                ...prev[item.id],
                                                                duedate: iso,
                                                            },
                                                        }));
                                                    }}
                                                    className={styles.nativeDateInput}
                                                />
                                            )}
                                        </div>
                                    </div>
                                );
                            },
                        }
                    ]}

                />

            </div>

            {/* --- Meet Your Coaches Section --- */}

            <div className={styles.fullWidthSection}>
                <div className={styles.sectionHeaderLine}>
                    <h3 className={styles.sectionTitle}>Meet Your Coaches</h3>
                    <div className={styles.line}></div>
                </div>

                <div className={styles.coachesGrid}>
                    {!course?.faculty?.length && <p>No coaches assigned</p>}

                    {course?.faculty?.map((facultyItem: any, i: number) => {
                        const user = facultyItem.user;

                        return (
                            <div
                                key={facultyItem.id}
                                className={styles.coachCard}
                            >
                                <div className={styles.coachHeader}>
                                    <img
                                        src={
                                            user?.profilepicture || UserPlaceholder // fallback
                                        }
                                        alt={user?.fullname || 'Coach'}
                                        className={styles.coachImg}
                                    />

                                    <div className={styles.coachMainInfo}>
                                        <h4 className={styles.coachName}>
                                            {user?.fullname || 'Unknown Coach'}
                                        </h4>

                                        <p className={styles.coachRole}>
                                            {facultyItem.facultyrole || 'Coach'}
                                        </p>

                                        <div
                                            className={styles.coachSocials}
                                            style={{ display: 'none' }}
                                        >
                                            <Linkedin size={18} />
                                            <Mail size={18} />
                                        </div>
                                    </div>
                                </div>

                                <p className={styles.coachBio}>
                                    {user?.bio || 'No bio available'}
                                </p>
                            </div>
                        );
                    })}
                </div>
            </div>

            {/* --- FAQs & Terms Split Section --- */}
            <div className={`${styles.fullWidthSection} ${styles.bottomGrid}`}>
                {/* FAQs Left */}
                <EditableSection onEdit={() => setShowEditFaqModal(true)}>
                    <h3 className={styles.sectionTitle}>FAQs</h3>
                    <div className={styles.faqContainer}>
                        {isFaqLoading && <p>Loading FAQs...</p>}

                        {!isFaqLoading && faqs.length === 0 && (
                            <p className={styles.descriptionText}>
                                No FAQs available for this category yet.
                            </p>
                        )}

                        {paginatedFaqs.map((item, idx) => {
                            const globalIndex = (faqPage - 1) * FAQs_PER_PAGE + idx;
                            return (
                                <div key={item.id} className={styles.faqItem}>
                                    <div
                                        className={styles.faqHeader}
                                        onClick={() =>
                                            setActiveFaq(
                                                activeFaq === globalIndex
                                                    ? null
                                                    : globalIndex
                                            )
                                        }
                                    >
                                        {/* Display title/question from DB */}
                                        <span className={styles.faqQuestion}>
                                            {item.title}
                                        </span>
                                        <ChevronDown
                                            className={
                                                activeFaq === globalIndex
                                                    ? styles.rotate
                                                    : ''
                                            }
                                            size={18}
                                        />
                                    </div>

                                    {activeFaq === globalIndex && (
                                        <div className={styles.faqBody}>
                                            {/* Display content from DB */}
                                            <p>{item.content}</p>

                                            {/* Optional: Show if it was helpful (using your DB counts) */}
                                            <div
                                                style={{
                                                    marginTop: '10px',
                                                    fontSize: '12px',
                                                    color: '#666',
                                                }}
                                            >
                                                {item.helpfulcount} people found
                                                this helpful
                                            </div>
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </EditableSection>

                {totalFaqPages > 1 && (
                    <div className={styles.pagination}>
                        <button
                            disabled={faqPage === 1}
                            onClick={() => {
                                setFaqPage(p => p - 1);
                                setActiveFaq(null);
                            }}
                        >
                            Previous
                        </button>

                        {Array.from({ length: totalFaqPages }).map((_, i) => (
                            <button
                                key={i}
                                className={
                                    faqPage === i + 1 ? styles.activePage : ''
                                }
                                onClick={() => {
                                    setFaqPage(i + 1);
                                    setActiveFaq(null);
                                }}
                            >
                                {i + 1}
                            </button>
                        ))}

                        <button
                            disabled={faqPage === totalFaqPages}
                            onClick={() => {
                                setFaqPage(p => p + 1);
                                setActiveFaq(null);
                            }}
                        >
                            Next
                        </button>
                    </div>
                )}
            </div>

            <ResponsiveModal
                open={showDeleteConfirm}
                onClose={cancelDeleteSubmission}
            >
                <DeleteConfirmModal
                    onDelete={confirmDeleteSubmission}
                    onCancel={cancelDeleteSubmission}
                />
            </ResponsiveModal>

            <ResponsiveModal
                open={showDeleteResourceConfirm}
                onClose={cancelDeleteResource}
            >
                <ConfirmContent
                    title="Delete This Resource?"
                    // message="This action cannot be undone. Are you sure you want to delete this resource?"
                    confirmText="Delete"
                    cancelText="Cancel"
                    onConfirm={confirmDeleteResource}
                    onCancel={cancelDeleteResource}
                />
            </ResponsiveModal>

            <ResponsiveModal
                open={resourceSuccessModal.open}
                onClose={() =>
                    setResourceSuccessModal(prev => ({ ...prev, open: false }))
                }
            >
                <SuccessContent
                    title={resourceSuccessModal.title}
                    subtitle={resourceSuccessModal.subtitle}
                />
            </ResponsiveModal>


            <ResponsiveModal
                elevated
                open={showSuccessModal}
                onClose={() => setShowSuccessModal(false)}
            >
                <SuccessModal />
            </ResponsiveModal>

            <ResponsiveModal
                elevated
                open={showErrorModal}
                onClose={() => setShowErrorModal(false)}
            >
                <ErrorPopupModal
                    title={errorMessage}
                    onCancel={() => setShowErrorModal(false)}
                />
            </ResponsiveModal>

            <ResponsiveModal
                open={showAssignmentModal}
                onClose={() => setShowAssignmentModal(false)}
            >
                <AssignmentForm
                    initialData={
                        editingAssessment
                            ? {
                                title: editingAssessment.title,
                                dueDate: editingAssessment.duedate,
                                type: editingAssessment.allowedfiletypes?.[0] || '',
                                isVisible: editingAssessment.allowfilesubmission,
                            }
                            : undefined
                    }
                    isEditMode={!!editingAssessment}
                    assignmentCreateErrors={assignmentCreateErrors}
                    onSubmit={async (assignments) => {

                        // 🔥 IN EDIT MODE: Use only first assignment (single update)
                        if (editingAssessment) {
                            const assignment = assignments[0];
                            try {
                                await updateAssessment(editingAssessment.id, {
                                    title: assignment.title,
                                    duedate: assignment.dueDate
                                        ? new Date(assignment.dueDate).toISOString()
                                        : undefined,
                                    allowfilesubmission: assignment.isVisible,
                                    allowedfiletypes: FILE_TYPE_MAP[assignment.type.toLowerCase()] ?? [],
                                    lessonid: activeAssignmentLessonFolder?.id,
                                });

                                setAssignmentSuccessModal({
                                    open: true,
                                    title: "Assignment Updated Successfully",
                                    subtitle: "The assignment has been updated."
                                });

                                setEditingAssessment(null);
                                setShowAssignmentModal(false);
                                await refetchAssessments();

                                setTimeout(() => {
                                    setAssignmentSuccessModal(prev => ({ ...prev, open: false }));
                                }, 3000);
                            } catch (err: any) {
                                showError(err);
                            }
                            return;
                        }

                        // 🔥 IN CREATE MODE: Batch create multiple assignments with error tracking
                        const createdIds = new Set<string>();
                        const failedAssignments = new Map<string, string>();

                        for (const assignment of assignments) {
                            try {
                                await create({
                                    courseid: id,
                                    lessonid: activeAssignmentLessonFolder?.id,
                                    title: assignment.title,
                                    duedate: new Date(assignment.dueDate).toISOString(),
                                    allowfilesubmission: assignment.isVisible,
                                    requirefilesubmission: true,
                                    allowedfiletypes: FILE_TYPE_MAP[assignment.type.toLowerCase()] ?? [],
                                });
                                createdIds.add(assignment.id);
                            } catch (err: any) {
                                const errorMsg = getApiErrorMessage(err);
                                failedAssignments.set(assignment.id, errorMsg);
                            }
                        }

                        // 🔥 If no failures, show success and close
                        if (failedAssignments.size === 0) {
                            setAssignmentCreateErrors({});
                            setAssignmentSuccessModal({
                                open: true,
                                title: assignments.length === 1
                                    ? "Assignment Created Successfully"
                                    : `${assignments.length} Assignments Created Successfully`,
                                subtitle: "The assignment(s) have been added to the program."
                            });

                            setShowAssignmentModal(false);
                            await refetchAssessments();

                            setTimeout(() => {
                                setAssignmentSuccessModal(prev => ({ ...prev, open: false }));
                            }, 3000);
                            return;
                        }

                        // 🔥 If some or all failed: track errors and keep modal open for removal/retry
                        setAssignmentCreateErrors(Object.fromEntries(failedAssignments));

                        const successCount = createdIds.size;
                        const failureCount = failedAssignments.size;

                        if (successCount > 0) {
                            showError(
                                `${successCount} created, ${failureCount} failed. Remove failed items and try again.`
                            );
                            await refetchAssessments();
                        } else {
                            showError(
                                `All ${failureCount} assignments failed to create. Fix errors and try again.`
                            );
                        }
                    }}

                    onClose={() => setShowAssignmentModal(false)}
                />
            </ResponsiveModal>

            <ResponsiveModal
                open={showDeleteAssignmentConfirm}
                onClose={() => setShowDeleteAssignmentConfirm(false)}
            >
                <ConfirmContent
                    title="Are you sure you want to delete this assignment?"
                    confirmText="Delete"
                    cancelText="Cancel"
                    onConfirm={confirmDeleteAssignment}
                    onCancel={() => setShowDeleteAssignmentConfirm(false)}
                />
            </ResponsiveModal>

            <ResponsiveModal
                open={assignmentSuccessModal.open}
                onClose={() =>
                    setAssignmentSuccessModal(prev => ({ ...prev, open: false }))
                }
            >
                <SuccessContent
                    title={assignmentSuccessModal.title}
                    subtitle={assignmentSuccessModal.subtitle}
                />
            </ResponsiveModal>

            {showProgramGoalsModal && (
                <ProgramGoalsModal
                    isOpen={showProgramGoalsModal}
                    onClose={() => setShowProgramGoalsModal(false)}
                    userId={user.id}
                    programId={id}
                    usedGoalTypeIds={usedGoalTypeIds}
                    onSubmitGoal={async (data) => {
                        const result = await createGoal({
                            userid: user.id,
                            programid: id,
                            title: data.goalType,
                            description: data.description,
                            goaltype: data.goalType,
                            goaltypeid: data.goalTypeId,
                            targetvalue: data.targetValue
                                ? Number(data.targetValue)
                                : undefined,
                            unit: data.unit,
                            measurementtype: 'numeric',
                            istemplate: true,
                        });

                        if (!result.success) {
                            throw new Error(
                                result.message || 'Failed to create goal'
                            );
                        }

                        await fetchGoals();
                        await fetchGoalTypes();

                        showSuccess(
                            'Goal Created Successfully',
                            'Program goal has been added.'
                        );

                        setShowProgramGoalsModal(false);
                    }}

                    onAddCustomGoalType={async (newType) => {
                        console.log('New Goal Type:', newType);

                        // Future API Integration Here
                    }}
                />
            )}

            {showEditGoalsModal && (
                <ResponsiveModal
                    open={showEditGoalsModal}
                    onClose={() => setShowEditGoalsModal(false)}
                >
                    <EditProgramGoals
                        goals={goals}
                        goalTypes={goalTypes}
                        units={initialUnits}
                        updateGoal={updateGoal}
                        createGoal={createGoal}
                        deleteGoal={deleteGoal}
                        fetchGoals={fetchGoals}
                        fetchGoalTypes={fetchGoalTypes}
                        userId={user.id}
                        programId={id ?? ''}
                        onClose={() => setShowEditGoalsModal(false)}
                        onSuccess={showSuccess}
                        onError={showError}
                    />
                </ResponsiveModal>
            )}
            {showEditInfoModal && (
                <EditProgramInfo
                    onClose={() => setShowEditInfoModal(false)}
                    course={course}
                    onSaveSuccess={async () => {
                        await refreshDetails(); // 🔥 THIS IS THE FIX

                        setCoverPreview(
                            `http://localhost:5000/api/course/${course.id}/cover?t=${Date.now()}`
                        );

                        setshowEditOwnerModal(false);

                        showSuccess(
                            'Program Updated',
                            'Your changes have been saved successfully.'
                        );
                    }}
                />
            )}

            {showEditOwnerModal && (
                <OwnerModal
                    course={course}
                    onCancel={() => setshowEditOwnerModal(false)}
                    onSaveSuccess={async () => {
                        await refreshDetails(); // 🔥 THIS IS THE FIX

                        setshowEditOwnerModal(false);

                        showSuccess(
                            'Program Updated',
                            'Your changes have been saved successfully.'
                        );
                    }}
                />
            )}


            {showEditLearningsModal && (
                <LearningsModal
                    onCancel={() => setshowEditLearningsModal(false)}
                    onSave={async (data) => {
                        if (!course?.id) return;

                        const payload = {
                            programincludes: data.programIncludes,
                            learningtakeaways: data.takeaways,
                        };

                        await updateCourse(course.id, payload);

                        setshowEditLearningsModal(false);

                        await refreshDetails();

                        showSuccess(
                            'Program Updated Successfully',
                            'Program includes and learnings have been updated.'
                        );
                    }}
                    initialIncludes={course.programincludes || []}
                    initialTakeaways={course.learningtakeaways || []}
                />
            )}
            {showEditFaqModal && (
                <FaqModal
                    onClose={() => setShowEditFaqModal(false)}
                    initialFaqs={mappedFaqs}
                    onSave={handleFaqSave}
                />
            )}

            {isResourceModalOpen && (
                <ResourceModal
                    files={stagedFiles}
                    onClose={() => setIsResourceModalOpen(false)}
                    onAddFile={handleAddFileClick}
                    onDeleteFile={handleDeleteFile}
                    onToggleVisibility={handleToggleVisibility}
                    onToggleDownloadable={handleToggleDownloadable}
                    onSubmit={handleResourceSubmit}
                />
            )}

            <ResponsiveModal
                open={showLearnerUploads}
                onClose={() => setShowLearnerUploads(false)}
            >
                <ViewLearnerUploads
                    title="View Learner Uploads"
                    uploads={learnerUploads}
                    isLoading={submissionsLoading}
                    isReviewing={reviewingSubmission}
                    onClose={() => setShowLearnerUploads(false)}
                    onView={handleViewLearnerUpload}
                    onDownload={upload => {
                        console.log(
                            '📥 Learner upload selected:',
                            upload
                        );

                        setSelectedLearnerUpload(upload);
                        setShowLearnerDownloadConfirm(true);
                    }}
                    onSaveApproval={handleSaveLearnerApproval}
                />
            </ResponsiveModal>

            <ResponsiveModal
                elevated
                open={submissionReviewSuccess.open}
                onClose={() =>
                    setSubmissionReviewSuccess(prev => ({
                        ...prev,
                        open: false,
                    }))
                }
            >
                <SuccessContent
                    title={submissionReviewSuccess.title}
                    subtitle={submissionReviewSuccess.subtitle}
                />
            </ResponsiveModal>

            <ResponsiveModal
                open={showLearnerDownloadConfirm}
                onClose={() => {
                    setShowLearnerDownloadConfirm(false);
                    setSelectedLearnerUpload(null);
                }}
            >
                <ConfirmDownloadModal
                    onConfirm={async () => {
                        if (!selectedLearnerUpload) {
                            return;
                        }

                        // ✅ Close modal immediately on Download click
                        const fileId = selectedLearnerUpload.fileId;

                        setShowLearnerDownloadConfirm(false);
                        setSelectedLearnerUpload(null);

                        try {
                            const result = await downloadLearnerFile(fileId);

                            if (!result) {
                                return;
                            }

                            const link = document.createElement('a');

                            link.href = result.file;
                            link.download = result.filename;

                            document.body.appendChild(link);
                            link.click();
                            document.body.removeChild(link);
                        } catch (err) {
                            console.error(
                                '❌ Learner file download failed:',
                                err
                            );

                            showError(
                                getApiErrorMessage(err)
                            );
                        }
                    }}
                    onCancel={() => {
                        setShowLearnerDownloadConfirm(false);
                        setSelectedLearnerUpload(null);
                    }}
                />
            </ResponsiveModal>
            <ResponsiveModal
                open={successModal.open}
                onClose={() => setSuccessModal(prev => ({ ...prev, open: false }))}
            >
                <SuccessContent
                    title={successModal.title}
                    subtitle={successModal.subtitle}
                />
            </ResponsiveModal>

        </div>
    );
};

export default AdminEditProgramPage;
